import { useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ShoppingCart, CheckCircle2, Shield, Truck, Award } from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO, { productJsonLd } from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import { findProduct, relatedProducts } from "@/lib/catalog";
import { productImages, pickImage } from "@/lib/cdn";
import { useT } from "@/lib/i18n";

const ProductPage = () => {
  const t = useT();
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = useMemo(() => findProduct(slug), [slug]);
  const related = useMemo(() => relatedProducts(slug, 4), [slug]);

  if (!product) {
    return (
      <Layout testId="page-product-404">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-40 text-center">
          <div className="font-display text-[28px] mb-3">{t("product.not_found")}</div>
          <Link to="/maps" className="btn-primary inline-flex">{t("cta.explore_maps")} <ArrowRight size={16} /></Link>
        </div>
      </Layout>
    );
  }

  const buyNow = () => navigate(`/thank-you/${product.slug}`);
  const cdn = productImages(product.slug);
  const ogImage = cdn.og;
  const url = typeof window !== "undefined" ? `${window.location.origin}/product/${product.slug}` : "";

  return (
    <Layout testId="page-product">
      <SEO
        title={product.name}
        description={product.valueLine || product.tagline}
        image={pickImage(ogImage)}
        type="product"
        jsonLd={productJsonLd(product, { url, imageUrl: ogImage })}
      />
      <section className="pt-10 md:pt-14 pb-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Breadcrumb items={[{ to: "/", label: t("common.home") }, { to: "/maps", label: t("nav.maps") }, { label: product.shortName || product.region }]} />
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6 reveal order-2 lg:order-1">
            <div className="text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">{product.country} · {product.region}</div>
            <h1 className="font-display text-[36px] md:text-[52px] leading-[1.05] text-[color:var(--gv-ink)] mb-4">
              {product.name}
            </h1>
            <p className="text-[16px] text-[color:var(--gv-ink)]/75 mb-4 italic">{product.tagline}</p>

            <div className="flex items-center gap-3 mb-6">
              <div className="text-[28px] font-display text-[color:var(--gv-ink)]">€{product.priceFrom}</div>
              <div className="text-[14px] text-[color:var(--gv-ink)]/50">– €{product.priceTo}</div>
            </div>

            <div className="flex items-center gap-1.5 text-[color:var(--gv-pink)] mb-6">
              {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
              <span className="ml-2 text-[13px] text-[color:var(--gv-ink)]/70">{t("product.rating")}</span>
            </div>

            <div className="space-y-2 mb-8 text-[14px]">
              <Trust Icon={Shield} label={t("trust.secure_payment")} />
              <Trust Icon={Truck} label={t("trust.worldwide_shipping")} />
              <Trust Icon={Award} label={t("trust.premium_print")} />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <button onClick={buyNow} className="btn-primary flex-1" data-testid="product-buy-now">
                {t("cta.buy_now")} <ArrowRight size={16} />
              </button>
              <button className="btn-secondary flex-1" data-testid="product-add-cart">
                <ShoppingCart size={15} /> {t("cta.add_cart")}
              </button>
            </div>

            <div className="text-[13.5px] text-[color:var(--gv-ink)]/70 leading-relaxed">
              {product.valueLine}
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="rounded-[6px] overflow-hidden border border-[color:var(--gv-line)] bg-[color:var(--gv-mist)] shadow-[0_40px_80px_-40px_rgba(46,80,103,0.35)]">
              <img src={product.image} alt={product.name} className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Value block */}
      <section className="py-16 bg-[color:var(--gv-mist)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center reveal">
          <h2 className="font-display text-[28px] md:text-[40px] text-[color:var(--gv-ink)] mb-3">
            {t("product.value_title_a")} <span className="italic text-[color:var(--gv-pink)]">{t("product.value_title_b")}</span> <span aria-hidden>🍷</span>
          </h2>
          <p className="text-[15.5px] text-[color:var(--gv-ink)]/70 max-w-2xl mx-auto">
            {t("product.value_text")}
          </p>
        </div>
      </section>

      {/* Learning + Play cross-sells */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Link to="/learn" className="reveal bg-white border border-[color:var(--gv-line)] rounded-[5px] p-7 hover:border-[color:var(--gv-pink)] transition-colors">
            <div className="eyebrow mb-2">{t("nav.learn")}</div>
            <div className="font-display text-[22px] text-[color:var(--gv-ink)] mb-2">{t("product.learn_this_region")}</div>
            <div className="text-[13.5px] text-[color:var(--gv-ink)]/70 mb-5">{t("product.learn_this_region_text", { region: product.region })}</div>
            <span className="link-arrow text-[13.5px]">{t("cta.start_learning")} <ArrowRight size={14} /></span>
          </Link>
          <Link to="/play" className="reveal bg-white border border-[color:var(--gv-line)] rounded-[5px] p-7 hover:border-[color:var(--gv-pink)] transition-colors">
            <div className="eyebrow mb-2">{t("nav.play")}</div>
            <div className="font-display text-[22px] text-[color:var(--gv-ink)] mb-2">{t("product.test_knowledge")}</div>
            <div className="text-[13.5px] text-[color:var(--gv-ink)]/70 mb-5">{t("product.test_knowledge_text", { region: product.region })}</div>
            <span className="link-arrow text-[13.5px]">{t("cta.take_quiz")} <ArrowRight size={14} /></span>
          </Link>
        </div>
      </section>

      {/* Details */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 reveal">
          <h3 className="font-display text-[22px] md:text-[26px] text-[color:var(--gv-ink)] mb-5">{t("product.details")}</h3>
          <ul className="space-y-2.5 text-[14.5px] text-[color:var(--gv-ink)]/80">
            {[t("product.detail1"), t("product.detail2"), t("product.detail3")].map((d) => (
              <li key={d} className="flex items-start gap-2"><CheckCircle2 size={17} className="mt-0.5 text-[color:var(--gv-pink)]" />{d}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-20 bg-[color:var(--gv-mist)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <h3 className="font-display text-[26px] md:text-[34px] text-[color:var(--gv-ink)] mb-8 reveal">{t("common.you_might_also")}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {related.map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center reveal">
          <h3 className="font-display text-[30px] md:text-[42px] text-[color:var(--gv-ink)] mb-6">
            {t("home.final.title")} <span aria-hidden>🍷</span>
          </h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/maps" className="btn-primary">{t("cta.explore_maps")} <ArrowRight size={16} /></Link>
            <Link to="/learn" className="btn-secondary">{t("cta.start_learning")} <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

const Trust = ({ Icon, label }) => (
  <div className="flex items-center gap-2 text-[color:var(--gv-ink)]/80">
    <Icon size={16} className="text-[color:var(--gv-pink)]" />
    <span>{label}</span>
  </div>
);

export default ProductPage;
